import React, { useEffect, useRef, useState } from 'react';
import Type from 'prop-types';
import { noop } from 'lodash';
import { Divider } from "@mui/material";
import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import cn from 'classnames';
import { DueDate } from 'components';
import { useTranslation } from 'hooks';
import { CookieService } from 'services';
import styles from './TaskItem.module.css';

const TaskItem = (props) => {
  const { task, showDate, showTime, onSelect, positionNumber, taskListRef } = props;
  const [nodeRefWidth, setNodeRefWidth] = useState();

  const [taskBounceShow, setTaskBounceShow] = useState(false);
  const [swiperActionsVisible, setSwiperActionsVisible] = useState(false);

  const nodeRef = useRef(null);
  const cookies = new CookieService();

  const { t } = useTranslation();
  let lastTouchPoint = { x: null, y: null };

  let trackPosX = 0;
  let animation;

  useEffect(() => {
    if (positionNumber === 0) {
      const taskBounceShownValue = cookies.get('taskBounceShow');
      if (!taskBounceShownValue) {
        cookies.set('taskBounceShow', true);
        setTaskBounceShow(true);
      }
    }

    if (nodeRef.current) {
      setNodeRefWidth(nodeRef.current.clientWidth / 4);
    }
  }, [cookies, positionNumber]);

  useEffect(() => {
    return () => {
      clearAllBodyScrollLocks();
    };
  }, []);

  const handleClick = () => {
    onSelect(task.id);
  };

  const handleTouchMove = (event) => {
    const touchX = event.touches[0].clientX;
    const touchY = event.touches[0].clientY;
    setSwiperActionsVisible(true);

    if (lastTouchPoint.x === null) {
      lastTouchPoint.x = touchX;
      lastTouchPoint.y = touchY;
      return;
    }

    const deltaX = touchX - lastTouchPoint.x;

    const deltaY = Math.abs(touchY - lastTouchPoint.y);

    // не двигаем при скролле страницы
    if (deltaY > 20) {
      return;
    }
    window.swipeStarted = true;

    disableBodyScroll(taskListRef.current);

    const node = nodeRef.current;
    if (!node) return;
    trackPosX += deltaX;
    lastTouchPoint = { x: touchX, y: touchY };

    if (trackPosX < 0) return;

    if (trackPosX >= nodeRefWidth) {
      trackPosX = nodeRefWidth;
    }
    if (animation) {
      cancelAnimationFrame(animation);
    }
    const frame = () => {
      node.style.transform = `translateX(${trackPosX}px)`;
      node.style.transition = '';
    };
    animation = requestAnimationFrame(frame);
  };

  const handleTouchEnd = (event) => {
    enableBodyScroll(taskListRef.current);

    lastTouchPoint.x = null;
    window.swipeStarted = false;
    const { changedTouches } = event;

    if (!changedTouches[0]) return;

    const node = nodeRef.current;
    if (!node) return;

    if (trackPosX >= nodeRefWidth - 10) {
      node.style.transform = `translateX(${nodeRefWidth}px)`;
      trackPosX = nodeRefWidth;
      setSwiperActionsVisible(true);
    } else {
      node.style.transform = `translateX(0px)`;
      trackPosX = 0;
      setSwiperActionsVisible(false);
    }
    if (animation) {
      cancelAnimationFrame(animation);
    }
  };

  const cancelSwipe = () => {
    const node = nodeRef.current;
    node.style.transform = `translateX(0px)`;
    trackPosX = 0;
  };

  const renderAssignees = () => {
    const names = task?.executors?.map((assignee) => assignee.name);
    if (names && names.length > 0) {
      return `${task.executorText}, ${names.join(', ')}`;
    }

    return task.executorText;
  };

  const renderSecondaryText = () => {
    return (
      <div className={styles.secondaryText}>
        <DueDate
          due={task.deadline}
          showDate={showDate}
          showTime={showTime}
          dataTestId="taskDate"
        />
        <p data-testid="taskCustomer">{`${t('customer')}: ${task?.customerText}`}</p>
        <p data-testid="taskAuthor">{`${t('author')}: ${task?.authorText}`}</p>
        <p data-testid="taskAssignees">{`${t('assignees')}: ${renderAssignees()}`}</p>
      </div>
    );
  };
  return (
    <>
      <div className={styles.swiperWrapper}>
        <div
          role="presentation"
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onClick={swiperActionsVisible ? cancelSwipe : handleClick}
          className={cn(styles.task, {
            [styles.bounceTask]: taskBounceShow && positionNumber === 0,
          })}
          ref={nodeRef}
        >
          <div className={styles.description__wrapper}>
            <div className={styles.description}>
              <span className={styles.header}>{task.firstMessage}</span>
              <div>{renderSecondaryText()}</div>
            </div>
          </div>
        </div>
      </div>
      <Divider />
    </>
  );
};

TaskItem.propTypes = {
  task: Type.shape({
    id: Type.string.isRequired,
    createdAt: Type.string.isRequired,
    firstMessage: Type.string,
    lastMessage: Type.string,
    customerId: Type.string.isRequired,
    customerText: Type.string.isRequired,
    authorId: Type.string.isRequired,
    authorText: Type.string.isRequired,
    executorId: Type.string.isRequired,
    executorText: Type.string.isRequired,
    deadline: Type.string.isRequired,
    constructionId: Type.string,
    constructionText: Type.string,
    type: Type.string,
    messagesCount: Type.number.isRequired,
    executors: Type.arrayOf(
      Type.shape({
        todoid: Type.string.isRequired,
        executorName: Type.string.isRequired,
        executor: Type.string.isRequired,
        assignerName: Type.string.isRequired,
        assigner: Type.string.isRequired,
        done: Type.string,
        doneDatetime: Type.string,
      }),
    ),
    timestamp: Type.string,
  }).isRequired,
  showDate: Type.bool,
  showTime: Type.bool,
  onSelect: Type.func,
  positionNumber: Type.string,
  // eslint-disable-next-line react/forbid-prop-types
  taskListRef: Type.object.isRequired,
};

TaskItem.defaultProps = {
  onSelect: noop,
  showDate: false,
  showTime: false,
  positionNumber: null,
};

export default TaskItem;
